import { default as http } from "../rest/http";
import { SortDirection, UserSortKey } from "../constants/users";

// Global user list endpoints (ADM-89). camelCase like the v3 organization
// endpoints; see ADM-89-user-list-api-handoff.md for the full contract.
const BASE_PATH = "/master-data/v3/users";

export interface UserListFilters {
  role: string[];
  status: string[];
  organizationId: number | null; // that org and all of its sub-orgs
  search: string;
}

export interface UsersV3QueryParams {
  page: number;
  perPage: number;
  filters: UserListFilters;
  sort: { by: UserSortKey; dir: SortDirection } | null; // null = server default order
}

// `allMatching` keys are all optional ({} = everyone in scope); only the
// filters that are set are sent.
export interface AllMatchingFilters {
  role?: string[];
  status?: string[];
  organizationId?: number;
  search?: string;
}

// Either an explicit set of users, or "Select within all pages": every user
// matching the filters, resolved by the server, minus any the admin unticked.
export type UserSelection =
  | { userIds: string[] }
  | { allMatching: AllMatchingFilters; excludeUserIds?: string[] };

const appendFilters = (query: URLSearchParams, filters: UserListFilters) => {
  filters.role.forEach((role) => query.append("role", role));
  filters.status.forEach((status) => query.append("status", status));
  if (filters.organizationId != null) {
    query.append("organizationId", String(filters.organizationId));
  }
  if (filters.search) query.append("search", filters.search);
};

// GET /master-data/v3/users — one page of users across every org in scope.
export const fetchUsersV3 = async (params: UsersV3QueryParams) => {
  const query = new URLSearchParams();
  query.append("page", String(params.page));
  query.append("perPage", String(params.perPage));
  if (params.sort) {
    query.append("sortBy", params.sort.by);
    query.append("sortDir", params.sort.dir);
  }
  appendFilters(query, params.filters);

  const response = await http.get(`${BASE_PATH}?${query.toString()}`);
  return response;
};

// GET /master-data/v3/users/stats — the four summary cards, scoped to the caller
// and independent of the table filters.
export const fetchUsersV3Stats = async () => {
  const response = await http.get(`${BASE_PATH}/stats`);
  return response;
};

// POST /master-data/v3/users/bulk/move — users keep their account data; users
// already in the destination are skipped (reported in `skipped`), not failed.
export const bulkMoveUsers = async (selection: UserSelection, destinationOrganizationId: number) => {
  const response = await http.post(`${BASE_PATH}/bulk/move`, {
    selection,
    destinationOrganizationId,
  });
  return response;
};

// POST /master-data/v3/users/bulk/deactivate — one-way here (no bulk
// reactivate); the caller is never deactivated (SELF skip) and no email is sent.
export const bulkDeactivateUsers = async (selection: UserSelection) => {
  const response = await http.post(`${BASE_PATH}/bulk/deactivate`, { selection });
  return response;
};
